import { jwtDecode } from "jwt-decode";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getErrorDetails } from "../app/appUtils";
import {
  AuthPermission,
  AuthPermissionAction,
  AuthUser,
} from "../features/auth/@types";
import {
  useGetUserPermissionQuery,
  useGetUserQuery,
} from "../features/auth/authApiSlice";
import {
  selectAuthError,
  selectAuthLoading,
  selectCurrentToken,
  selectCurrentUser,
  selectIsAuthenticated,
  selectUserPermissions,
  setError,
  setLoading,
  setPermissions,
  setUser,
} from "../features/auth/authSlice";

interface DecodedToken {
  id: string;
  iat: number;
  exp: number;
}

interface UseAuthReturn {
  user: AuthUser | null;
  permissions: AuthPermission[];
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  hasPermission: (
    resourceName: string,
    action?: AuthPermissionAction
  ) => boolean;
  refetch: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const user = useSelector(selectCurrentUser);
  const permissions = useSelector(selectUserPermissions);
  const isLoading = useSelector(selectAuthLoading);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const error = useSelector(selectAuthError);

  const userId = useMemo(() => {
    if (!token) return null;
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      return decoded.id;
    } catch (error) {
      console.error("Invalid token:", error);
      dispatch(setError("Invalid authentication token"));
      return null;
    }
  }, [token, dispatch]);

  const {
    data: userData,
    isLoading: userLoading,
    error: userError,
    refetch: refetchUser,
  } = useGetUserQuery(userId!, {
    skip: !userId,
    pollingInterval: 300000, // 5 minutes
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  const {
    data: permissionData,
    isLoading: permissionLoading,
    error: permissionError,
    refetch: refetchPermissions,
  } = useGetUserPermissionQuery(userId!, {
    skip: !userId,
    pollingInterval: 300000, // 5 minutes
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  // Update Redux state when API data changes
  useEffect(() => {
    dispatch(setLoading(userLoading || permissionLoading));
  }, [dispatch, userLoading, permissionLoading]);

  useEffect(() => {
    if (userData?.data && userData.data !== user) {
      dispatch(setUser(userData.data));
    }
  }, [dispatch, userData, user]);

  useEffect(() => {
    if (permissionData?.data && permissionData.data !== permissions) {
      dispatch(setPermissions(permissionData.data));
    }
  }, [dispatch, permissionData, permissions]);

  useEffect(() => {
    if (userError || permissionError) {
      const errorMessage =
        getErrorDetails(userError).message ||
        getErrorDetails(permissionError).message ||
        "Authentication error";
      dispatch(setError(errorMessage));
    } else {
      dispatch(setError(null));
    }
  }, [dispatch, userError, permissionError]);

  const hasPermission = (
    resourceName: string,
    action?: AuthPermissionAction
  ): boolean => {
    if (!permissions.length) return false;

    const permission = permissions.find(
      (p) => p.resourceName.toLowerCase() === resourceName.toLowerCase()
    );

    if (!permission) return false;
    if (!action) return true; // Just check if resource exists

    return permission.actions.includes(action);
  };

  const refetch = () => {
    refetchUser();
    refetchPermissions();
  };

  return {
    user,
    permissions,
    isLoading,
    isAuthenticated,
    error,
    hasPermission,
    refetch,
  };
};
